import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DateUtils } from '../person-edit/date-utils';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { switchMap, takeUntil } from "rxjs/operators";
import { of, Subject } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from '@angular/material/button';
import { FolderTreeComponent } from '../../components/folder-tree/folder-tree.component';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { ConfirmComponent, ConfirmResult } from '../../components/confirm/confirm.component';
import { SharepointSiteDto, SharepointFolderDto, PersonsApiService, SharePointApiService, GetSharePointFoldersFromSiteCommandResponse, CreatePersonCommandPersonDTO } from '../../../../../api/generated';
import { ModelToFormGroup, loadingObservable, nullOrUndefined } from '../../../../../shared/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { NgClass } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FolderService } from '../../services/folder.service';

@Component({
  selector: 'app-person-create',
  templateUrl: './person-create.component.html',
  styleUrls: ['./person-create.component.scss'],
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [FormsModule, MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, MatFormField, MatLabel, MatInput, ReactiveFormsModule, MatError, MatProgressSpinner, MatSelect, MatOption, FolderTreeComponent, MatButton, MatDatepickerModule, MatFormFieldModule, MatInputModule, RouterLink, TranslateModule, NgClass,]
})
export class PersonCreateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly pageSize = 25;
  private streamParentFolder: SharepointFolderDto | null = null;
  private streamPage = 1;
  private streamedFolders: SharepointFolderDto[] = [];
  @Input() showCancelButton: boolean = true;
  @Output() isPersonCreated = new EventEmitter<boolean>();
  readonly minDate: Date = new Date();
  readonly maxDate: Date = new Date(new Date().setMonth(new Date().getMonth() + 6));
  sites: SharepointSiteDto[] = [];
  folders: SharepointFolderDto[] = [];
  selectedFolder: SharepointFolderDto | null = null;

  form = new FormGroup<ModelToFormGroup<CreatePersonCommandPersonDTO>>({
    firstName: new FormControl(null, [Validators.required, Validators.pattern(/^[a-zA-Z\s]*$/)]),
    lastName: new FormControl(null, [Validators.required, Validators.pattern(/^[a-zA-Z\s]*$/)]),
    email: new FormControl(null, [Validators.required,  Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]),
    siteId: new FormControl(null, [Validators.required]),
    rights: new FormControl('Unassigned', { validators: [Validators.required], nonNullable: true }),
    driveId: new FormControl(null, [Validators.required]),
    folderId: new FormControl(null, [Validators.required]),
    folderName: new FormControl(null, [Validators.required]),
    folderUrl: new FormControl(null, [Validators.required]),
    language: new FormControl(null, [Validators.required]),
    endDate: new FormControl({ value: null, disabled: true }),
    endDateType: new FormControl('7'),
    message: new FormControl(null),
  });

  ngOnInit(): void {
    this.translate.get(['download', 'upload']).subscribe(translations => {
      this.PersonRightsTypes = [
        { value: 'Download', text: translations['download'] },
        { value: 'Upload', text: translations['upload'] }
      ];
    });

    this.updateEndDate(this.form.controls.endDateType.value ?? null, this.form.controls.endDate.value);

    // Listen for changes to endDateType
    this.form.controls.endDateType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        this.updateEndDate(type ?? null, this.form.controls.endDate.value);
      });

    // Listen for changes to endDate only when custom is selected
    this.form.controls.endDate.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        if (this.form.controls.endDateType.value === 'custom' && val) {
          const dateObj = DateUtils.parseDate(val);
          if (dateObj) {
            const dateTimeString = DateUtils.getDateTimeString(dateObj);
            if (val !== dateTimeString) {
              this.form.controls.endDate.setValue(dateTimeString, { emitEvent: false });
            }
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Updates the endDate control based on the type and value.
   * Uses centralized DateUtils for formatting and parsing.
   */
  private updateEndDate(type: string | null, value: unknown) {
    this.form.controls.endDate.clearValidators();
    this.form.controls.endDate.updateValueAndValidity();

    if (type === 'custom') {
      this.form.controls.endDate.enable();
      this.form.controls.endDate.setValidators([Validators.required]);
      this.form.controls.endDate.updateValueAndValidity();
      if (value) {
        const dateObj = DateUtils.parseDate(value);
        if (dateObj) {
          const dateTimeString = DateUtils.getDateTimeString(dateObj);
          if (value !== dateTimeString) {
            this.form.controls.endDate.setValue(dateTimeString, { emitEvent: false });
          }
        }
      }
    } else {
      const days = parseInt(type ?? '', 10);
      if (!isNaN(days)) {
        const date = new Date();
        date.setDate(date.getDate() + days);
        const dateTimeString = DateUtils.getDateTimeString(date);
        this.form.controls.endDate.enable();
        this.form.controls.endDate.setValue(dateTimeString);
      }
    }
  }


  saving = false;
  loading: boolean = false;
  loadingSite: boolean = false;
  loadingSites: boolean = false;
  loadingMoreFolders: boolean = false;
  hasMoreFolders: boolean = false;
  showError: boolean = false;

  PersonRightsTypes: Array<{ value: string, text: string }> = [];

  get preventSubmit() {
    return this.loading || this.form.invalid || this.form.get('rights')?.value === 'Unassigned';
  }

  constructor(
    private router: Router,
    private personService: PersonsApiService,
    private sharepointService: SharePointApiService,
    private folderService: FolderService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
  ) {
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras.state as { rights: string } | undefined;
    if (state?.rights) {
      this.form.controls.rights.setValue(state.rights as 'Unassigned' | 'Download' | 'Upload');
    }
    this.getSites();
    this.observeSiteChanges();
    console.log(translate.getBrowserLang());
    this.form.controls.language.setValue(translate.getBrowserLang() == "nl" ? "Dutch" : "English");
  }


  openSnackBar(message: string) {
    this.translate.get('close').subscribe((close: string) => {
      this.snackBar.open(message, close, { duration: 5000, horizontalPosition: 'end', verticalPosition: 'top' });
    });
  }

  openConfirmDialog(): void {
    this.translate.get(['personCreate.popUpTitle', 'personCreate.popUpContent']).subscribe(translations => {
      const dialogRef = this.dialog.open(ConfirmComponent, {
        width: '600px',
        data: {
          title: translations['personCreate.popUpTitle'],
          content: translations['personCreate.popUpContent'],
        },
      });

      dialogRef.afterClosed().subscribe(async (result: ConfirmResult) => {
        if (result.confirmed) {
          this.createPerson();
        }
      });
    });
  }

  submit() {
    if (this.form.value['rights']) {
      this.openConfirmDialog();
    } else {
      this.createPerson();
    }
  }

  getSites() {
    loadingObservable(this.sharepointService.getApiV10SharePointGetSites())
      .subscribe({
        next: (response) => {
          if ('loading' in response) {
            this.loadingSites = response.loading;
            if (response.loading) {
              this.form.controls.siteId.disable();
            } else {
              this.form.controls.siteId.enable();
            }
          } else {
            this.sites = response.items;
          }
        },
        error: (error) => {
          console.error('GET SHAREPOINT SITES REQUEST: ', error)
        }
      });
  }

  observeSiteChanges() {
    this.form.controls.siteId.valueChanges
      .pipe(
        switchMap((siteId) => {
          if (nullOrUndefined(siteId)) {
            return of({ items: [] } as GetSharePointFoldersFromSiteCommandResponse);
          }
          var site = this.sites.find(s => s.siteId === siteId);
          if (!site) {
            return of({ items: [] } as GetSharePointFoldersFromSiteCommandResponse);
          }
          return loadingObservable(this.sharepointService.postApiV10SharePointGetFolders({ siteId: site.siteId, teamId: site.teamId }));
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (response) => {
          if ('loading' in response) {
            this.loadingSite = response.loading;
          } else {
            this.initializeStreamFolders(response.items ?? []);
          }
        },
        error: (error) => {
          console.error('GET SHAREPOINT FOLDERS REQUEST: ', error);
        }
      })
  }

  onFolderSelected(folder: SharepointFolderDto | null) {
    if (folder === null) {
      this.selectedFolder = null;
      this.form.patchValue({
        driveId: null,
        folderId: null,
        folderName: null,
        folderUrl: null
      });
      return;
    }
    this.form.patchValue({
      driveId: folder.sharePointDriveId,
      folderId: folder.id,
      folderName: folder.name,
      folderUrl: folder.webUrl
    });

    if (folder.hasChildren && (!folder.subFolders || folder.subFolders.length === 0)) {
      this.loadChildrenForFolder(folder, 1);
    }
  }

  onLoadMoreFolders() {
    this.loadNextStreamPage();
  }

  onCreateFolderRequested(event: { parentFolder: SharepointFolderDto, folderName: string }) {
    const folderName = event.folderName.trim();
    if (!folderName) {
      return;
    }

    this.loading = true;
    this.sharepointService.postApiV10SharePointCreateFolder({
      newFolderName: folderName,
      sharePointSiteId: event.parentFolder.sharePointSiteId,
      sharePointDriveId: event.parentFolder.sharePointDriveId,
      sharePointFolderId: event.parentFolder.id
    }).subscribe({
      next: (response) => {
        this.loading = false;
        this.loadChildrenForFolder(event.parentFolder, 1, response.newFolderId);
      },
      error: (error) => {
        this.loading = false;
        console.error('CREATE NEW FOLDER IN SHAREPOINT ERROR: ', error);
      }
    });
  }

  private loadChildrenForFolder(folder: SharepointFolderDto, page: number, selectFolderId?: string) {
    loadingObservable(
      this.sharepointService.postApiV10SharePointGetChildrenFolderStream({
        folderId: folder.id,
        driveId: folder.sharePointDriveId,
        siteId: folder.sharePointSiteId,
        page
      })
    ).subscribe({
      next: (response) => {
        if ('loading' in response) {
          this.loading = response.loading;
        } else {
          folder.subFolders = response.items;

          if (selectFolderId) {
            const selectedFolder = this.folderService.findFolderById(selectFolderId, this.folders);
            if (selectedFolder) {
              this.selectedFolder = selectedFolder;
              this.onFolderSelected(selectedFolder);
            }
          }
        }
      },
      error: (error) => {
        console.error('GET SHAREPOINT CHILDREN REQUEST: ', error)
      }
    });
  }

  private initializeStreamFolders(items: SharepointFolderDto[]) {
    console.log('Initializing stream with folders: ', items);
    this.streamParentFolder = items[0] ?? null;
    this.streamPage = 1;
    this.streamedFolders = [];
    this.selectedFolder = null;
    console.log('Stream parent folder set to: ', this.streamParentFolder);
    if (!this.streamParentFolder) {
      this.folders = [];
      this.hasMoreFolders = false;
      return;
    }

    this.hasMoreFolders = true;
    this.folders = [];
    this.loadNextStreamPage();
  }

  private loadNextStreamPage() {
    if (this.loadingMoreFolders || !this.hasMoreFolders || !this.streamParentFolder) {
      return;
    }

    this.loadingMoreFolders = true;
    this.sharepointService.postApiV10SharePointGetChildrenFolderStream({
      siteId: this.streamParentFolder.sharePointSiteId,
      driveId: this.streamParentFolder.sharePointDriveId,
      folderId: this.streamParentFolder.id,
      page: this.streamPage
    }).subscribe({
      next: (response) => {
        const pageItems = response?.items ?? [];
        this.streamedFolders = [...this.streamedFolders, ...pageItems];
        this.folders = [...this.streamedFolders];
        this.streamPage += 1;
        this.hasMoreFolders = pageItems.length >= this.pageSize;
        this.loadingMoreFolders = false;
      },
      error: (error) => {
        this.loadingMoreFolders = false;
        this.hasMoreFolders = false;
        console.error('GET SHAREPOINT CHILDREN STREAM REQUEST: ', error);
      }
    });

  }

  createPerson() {
    this.showError = false;
    // this.loading = true;

    if (!this.form.valid) {
      //TODO: show error
    }

    const value = this.form.value;
    console.log(value);
    loadingObservable(
      this.personService.postApiV10Persons({
        person: {
          firstName: value.firstName ?? '',
          lastName: value.lastName ?? '',
          email: value.email ?? '',
          siteId: value.siteId ?? '',
          driveId: value.driveId ?? '',
          folderId: value.folderId ?? '',
          folderName: value.folderName ?? '',
          folderUrl: value.folderUrl ?? '',
          rights: value.rights,
          language: value.language ?? '',
          endDate: value.endDate ?? '',
          endDateType: value.endDateType ?? '',
          message: value.message ?? ''
        }
      })
    ).subscribe({
      next: (response) => {
        if ('loading' in response) {
          this.saving = response.loading;
          if (response.loading) {
            this.form.disable();
          } else {
            this.form.enable();
          }
        } else {
          this.router.navigate(['/', 'person-view']);
            this.translate.get(['emailSend', 'close']).subscribe(translations => {
              this.snackBar.open(translations['emailSend'], translations['close'], {
                duration: 5000,
                horizontalPosition: 'end',
                verticalPosition: 'top',
              });
            });
        }
      },
      error: (error) => {
        console.error('CREATE USER ERROR: ', error)
        this.showError = true;
      }
    });
  }
}
